import { useState, useEffect } from 'react';
import { Card, Btn, Modal, Input } from './UI';
import { changeSv, getUserLogin, supabase } from '../lib/supabase';

export const isVacantSv = (sv) => !!sv && String(sv.full_name || '').startsWith('Вакансия_');

// Управление доступом СВ: заменить на нового / уволить. Доступно ТМ (свои СВ) и админу (все).
export default function SvManage({ sv, actorId, onChanged }) {
  const [info, setInfo] = useState(null);       // { login, is_active, last_seen_at }
  const [mode, setMode] = useState(null);       // 'replace' | 'dismiss'
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);   // { login, full_name, action }
  const vacant = isVacantSv(sv);

  useEffect(() => { let on = true; getUserLogin(sv.user_id).then(i => { if (on) setInfo(i); }); return () => { on = false; }; }, [sv.id, sv.user_id]);

  function close() { setMode(null); setName(''); setErr(''); }

  async function run() {
    setBusy(true); setErr('');
    try {
      const res = await changeSv({ svId: sv.id, action: mode, newName: name, actorId });
      setResult(res);
      onChanged?.({ full_name: res.full_name, user_id: mode === 'dismiss' ? null : 'changed' }, res);
      setMode(null); setName('');
    } catch (e) { setErr(e.message || 'Ошибка'); }
    finally { setBusy(false); }
  }

  const lastSeen = info?.last_seen_at ? new Date(info.last_seen_at).toLocaleDateString('ru-RU') : null;

  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>ДОСТУП СВ</div>
          <div style={{ fontSize: 12.5, marginTop: 2 }}>
            {vacant ? 'СВ не назначен — входа нет' : info ? <>Логин: <b>{info.login}</b>{lastSeen && <span style={{ color: 'var(--text-muted)' }}> · последний вход {lastSeen}</span>}</> : 'Вход не создан'}
          </div>
        </div>
        <Btn size="sm" onClick={() => setMode('replace')}>{vacant ? 'Назначить СВ' : 'Заменить СВ'}</Btn>
        {!vacant && <Btn size="sm" color="ghost" onClick={() => setMode('dismiss')}>Уволить</Btn>}
      </div>

      <Modal open={!!mode} onClose={close} title={mode === 'dismiss' ? `Уволить: ${sv.full_name}` : vacant ? 'Назначить СВ' : `Заменить: ${sv.full_name}`}>
        <div style={{ padding: '0 20px 20px' }}>
          {mode === 'dismiss' ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 14 }}>
              Вход этого СВ будет закрыт сразу. Маршруты останутся в группе как «Вакансия_{sv.internal_code}», вся история сохранится.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 12 }}>
                {vacant ? 'Будет создан вход для нового СВ.' : 'Вход прежнего СВ будет закрыт, для нового создастся новый логин.'} Код СВ ({sv.internal_code}) и вся история маршрутов остаются.
              </div>
              <Input label="ФИО нового СВ" value={name} onChange={setName} placeholder="Иванова Мария Петровна" />
            </>
          )}
          {err && <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{err}</div>}
          <Btn size="full" color={mode === 'dismiss' ? 'danger' : 'primary'} onClick={run} disabled={busy || (mode === 'replace' && name.trim().length < 5)}>
            {busy ? 'Подождите...' : mode === 'dismiss' ? 'Уволить и закрыть вход' : vacant ? 'Назначить' : 'Заменить'}
          </Btn>
        </div>
      </Modal>

      <Modal open={!!result} onClose={() => setResult(null)} title={result?.login ? 'Новый логин создан' : 'Готово'}>
        <div style={{ padding: '0 20px 20px' }}>
          {result?.login ? (
            <>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>Передайте логин СВ <b>{result.full_name}</b>. Он показывается один раз, потом его можно увидеть в карточке СВ.</div>
              <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', wordBreak: 'break-all', marginBottom: 12 }}>{result.login}</div>
              <Btn size="full" onClick={() => { try { navigator.clipboard.writeText(result.login); } catch (e) { /* ignore */ } }}>Скопировать логин</Btn>
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>Вход закрыт. Маршруты остаются в группе как «{result?.full_name}».</div>
          )}
        </div>
      </Modal>
    </Card>
  );
}

// Для админа: выбрать любого СВ проекта и управлять им
export function SvManagePicker({ actorId }) {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let on = true;
    (async () => {
      const { data } = await supabase.from('supervisors').select('id, full_name, internal_code, user_id, tm_id').eq('is_active', true).order('full_name');
      if (on) setList(data || []);
    })();
    return () => { on = false; };
  }, [tick]);

  const sv = list.find(s => s.id === sel);
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Замена и увольнение СВ</div>
      <select value={sel} onChange={e => setSel(e.target.value)}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, marginBottom: 10, background: 'white' }}>
        <option value="">Выберите СВ…</option>
        {list.map(s => <option key={s.id} value={s.id}>{s.full_name} · {s.internal_code}</option>)}
      </select>
      {sv && <SvManage key={sv.id + (sv.user_id || '')} sv={sv} actorId={actorId} onChanged={() => setTick(t => t + 1)} />}
    </div>
  );
}
